/**
 * js/ui/icons.js — High-Plasticity Military Sci-Fi Vector Iconography
 * for VOID Conflict / Ultimate Card Game (UCG).
 *
 * Implements the Zero-Emojis Paradigm (docs/SLOT_ICON_PARADIGMA.md & CLAUDE.md):
 * Replaces all platform-inconsistent, cartoonish system emojis with
 * crisp, scalable, high-performance vector icons styled to the VOID
 * universe aesthetic.
 *
 * SIZING PARADIGM:
 * - viewBox: "0 0 16 16" (standardized across all icons)
 * - Default size: 16 (16x16px)
 * - Helper size presets:
 *   - 'xs': 10px
 *   - 'sm': 12px  (Stat badges, subscript pills, card footer indicators)
 *   - 'md': 14px  (Inline labels, cost badges, toast glyphs)
 *   - 'base': 16px (Buttons, segment filters, input adornments)
 *   - 'lg': 20px  (Modal headers, action triggers)
 *   - 'xl': 24px  (Mode selection tiles, prominent status badges)
 *
 * All icons include aria-hidden="true", focusable="false", and flex-shrink: 0
 * to guarantee rock-solid layout across all desktop & mobile viewports.
 */

const SIZE_PRESETS = {
  xs: 10,
  sm: 12,
  md: 14,
  base: 16,
  lg: 20,
  xl: 24
};

const ICON_REGISTRY = {
  // =========================================================================
  // CORE GAMEPLAY & DECKBUILDER
  // =========================================================================

  plasma: `<rect x="4" y="1.5" width="8" height="2" rx="1" fill="#64748b" stroke="#94a3b8" stroke-width="0.75"/>
    <rect x="3" y="3.5" width="10" height="9.5" rx="2.5" fill="#022c22" stroke="#10b981" stroke-width="1"/>
    <rect x="4.5" y="5.5" width="7" height="6" rx="1.5" fill="#00ff88"/>
    <path d="M5.5 6.5C5.5 6.5 6.5 8 8 7.5C9.5 7 10.5 8.5 10.5 8.5" stroke="#ffffff" stroke-width="0.8" stroke-linecap="round" opacity="0.85"/>
    <circle cx="6" cy="9.5" r="0.8" fill="#ffffff" opacity="0.7"/>
    <rect x="4" y="13" width="8" height="1.5" rx="0.75" fill="#64748b"/>`,

  ticket: `<path d="M1.5 4.5C1.5 3.67 2.17 3 3 3H13C13.83 3 14.5 3.67 14.5 4.5V6.5C13.67 6.5 13 7.17 13 8C13 8.83 13.67 9.5 14.5 9.5V11.5C14.5 12.33 13.83 13 13 13H3C2.17 13 1.5 12.33 1.5 11.5V9.5C2.33 9.5 3 8.83 3 8C3 7.17 2.33 6.5 1.5 6.5V4.5Z" fill="#831843" stroke="#f472b6" stroke-width="1" stroke-linejoin="round"/>
    <line x1="5.5" y1="4.5" x2="5.5" y2="11.5" stroke="#f472b6" stroke-width="0.9" stroke-dasharray="1.5 1.5" opacity="0.85"/>
    <circle cx="9.5" cy="8" r="1.8" fill="#fbcfe8" stroke="#f472b6" stroke-width="0.75"/>
    <path d="M8 5H11M8 11H11" stroke="#fbcfe8" stroke-width="0.75" stroke-linecap="round" opacity="0.8"/>`,

  hp: `<path d="M8 1.5L14 3.8V8.2C14 11.5 11.2 13.9 8 15C4.8 13.9 2 11.5 2 8.2V3.8L8 1.5Z" fill="#064e3b" stroke="#4ade80" stroke-width="1" stroke-linejoin="round"/>
    <path d="M8 4.2V11.8M4.2 8H11.8" stroke="#4ade80" stroke-width="1.8" stroke-linecap="round"/>
    <circle cx="8" cy="8" r="1" fill="#ffffff"/>`,

  attack: `<path d="M2 14L5.5 10.5M3 10.5L6.5 14" stroke="#f87171" stroke-width="1" stroke-linecap="round"/>
    <path d="M14 2L6 10L4.5 8.5L12.5 0.5L14 2Z" fill="#ef4444" stroke="#fca5a5" stroke-width="0.75" stroke-linejoin="round"/>
    <path d="M14 14L10.5 10.5M13 10.5L9.5 14" stroke="#f87171" stroke-width="1" stroke-linecap="round"/>
    <path d="M2 2L10 10L11.5 8.5L3.5 0.5L2 2Z" fill="#ef4444" stroke="#fca5a5" stroke-width="0.75" stroke-linejoin="round"/>`,

  kamikaze: `<polygon points="8,0.5 10,5 14.5,3.5 12,8 15.5,11 10.5,11 11,15.5 7.5,12 3.5,15 5,10.5 0.5,9.5 5,7 2,3.5 7,5" fill="#f97316" stroke="#fbbf24" stroke-width="0.8" stroke-linejoin="round"/>
    <circle cx="8" cy="8" r="2.5" fill="#fef08a"/>
    <circle cx="8" cy="8" r="1.2" fill="#ffffff"/>`,

  flying: `<path d="M1 8.5L8 1.5L15 8.5L11.5 10L8 5L4.5 10L1 8.5Z" fill="#0284c7" stroke="#38bdf8" stroke-width="0.9" stroke-linejoin="round"/>
    <path d="M3 11L8 6L13 11L11.5 13L8 9.5L4.5 13L3 11Z" fill="#0369a1" stroke="#7dd3fc" stroke-width="0.75" stroke-linejoin="round"/>
    <line x1="8" y1="2" x2="8" y2="14.5" stroke="#e0f2fe" stroke-width="0.8" stroke-linecap="round"/>`,

  ground: `<rect x="1" y="9.5" width="14" height="4.5" rx="2.25" fill="#1e293b" stroke="#94a3b8" stroke-width="0.9"/>
    <circle cx="3.5" cy="11.75" r="1" fill="#f97316"/>
    <circle cx="6.5" cy="11.75" r="1" fill="#94a3b8"/>
    <circle cx="9.5" cy="11.75" r="1" fill="#94a3b8"/>
    <circle cx="12.5" cy="11.75" r="1" fill="#f97316"/>
    <path d="M3.5 9.5L5.5 5.5H10.5L12.5 9.5H3.5Z" fill="#334155" stroke="#cbd5e1" stroke-width="0.8" stroke-linejoin="round"/>
    <rect x="7.5" y="3" width="1.5" height="3" fill="#f97316" stroke="#fed7aa" stroke-width="0.5"/>
    <line x1="8.25" y1="3" x2="13" y2="3" stroke="#f97316" stroke-width="1.2" stroke-linecap="round"/>`,

  air: `<circle cx="8" cy="8" r="6" stroke="#ef4444" stroke-width="1" stroke-dasharray="2.5 1.5" opacity="0.85"/>
    <circle cx="8" cy="8" r="3.5" fill="rgba(239, 68, 68, 0.2)" stroke="#f87171" stroke-width="0.8"/>
    <circle cx="8" cy="8" r="1.2" fill="#ffffff"/>
    <line x1="8" y1="0.5" x2="8" y2="3.5" stroke="#fca5a5" stroke-width="1.2" stroke-linecap="round"/>
    <line x1="8" y1="12.5" x2="8" y2="15.5" stroke="#fca5a5" stroke-width="1.2" stroke-linecap="round"/>
    <line x1="0.5" y1="8" x2="3.5" y2="8" stroke="#fca5a5" stroke-width="1.2" stroke-linecap="round"/>
    <line x1="12.5" y1="8" x2="15.5" y2="8" stroke="#fca5a5" stroke-width="1.2" stroke-linecap="round"/>`,

  target: `<circle cx="8" cy="8" r="6" stroke="#ef4444" stroke-width="1" stroke-dasharray="2.5 1.5" opacity="0.85"/>
    <circle cx="8" cy="8" r="3.5" fill="rgba(239, 68, 68, 0.2)" stroke="#f87171" stroke-width="0.8"/>
    <circle cx="8" cy="8" r="1.2" fill="#ffffff"/>
    <line x1="8" y1="0.5" x2="8" y2="3.5" stroke="#fca5a5" stroke-width="1.2" stroke-linecap="round"/>
    <line x1="8" y1="12.5" x2="8" y2="15.5" stroke="#fca5a5" stroke-width="1.2" stroke-linecap="round"/>
    <line x1="0.5" y1="8" x2="3.5" y2="8" stroke="#fca5a5" stroke-width="1.2" stroke-linecap="round"/>
    <line x1="12.5" y1="8" x2="15.5" y2="8" stroke="#fca5a5" stroke-width="1.2" stroke-linecap="round"/>`,

  target_ground: `<path d="M8 12L3.5 4.5H12.5L8 12Z" fill="#c2410c" stroke="#fb923c" stroke-width="0.8" stroke-linejoin="round"/>
    <line x1="2" y1="14" x2="14" y2="14" stroke="#fb923c" stroke-width="1.4" stroke-linecap="round"/>
    <circle cx="8" cy="7" r="1.1" fill="#fed7aa"/>`,

  target_air: `<path d="M8 1.5L13.5 11.5L9.5 10.2L8 7L6.5 10.2L2.5 11.5L8 1.5Z" fill="#0369a1" stroke="#38bdf8" stroke-width="0.85" stroke-linejoin="round"/>
    <circle cx="8" cy="5.8" r="1" fill="#e0f2fe"/>`,

  target_both: `<path d="M8 1.5L12 5.5H9.5V10.5H12L8 14.5L4 10.5H6.5V5.5H4L8 1.5Z" fill="#0f766e" stroke="#2dd4bf" stroke-width="0.8" stroke-linejoin="round"/>
    <circle cx="8" cy="8" r="1.1" fill="#ccfbf1"/>`,

  stealth_eye: `<path d="M1.5 8C1.5 8 4 3 8 3C12 3 14.5 8 14.5 8C14.5 8 12 13 8 13C4 13 1.5 8 1.5 8Z" fill="#3b0764" stroke="#c084fc" stroke-width="0.9" stroke-linejoin="round"/>
    <ellipse cx="8" cy="8" rx="3" ry="3" fill="#581c87" stroke="#e9d5ff" stroke-width="0.75"/>
    <line x1="4.5" y1="8" x2="11.5" y2="8" stroke="#f5d0fe" stroke-width="1.3" stroke-linecap="round"/>
    <circle cx="8" cy="8" r="0.9" fill="#ffffff"/>`,

  radar: `<circle cx="8" cy="8" r="6.5" stroke="#f59e0b" stroke-width="0.8" stroke-dasharray="2 1.2" opacity="0.6"/>
    <circle cx="8" cy="8" r="3.6" stroke="#f59e0b" stroke-width="0.9"/>
    <circle cx="8" cy="8" r="1.3" fill="#fde047"/>
    <line x1="8" y1="8" x2="12.5" y2="3.5" stroke="#fef08a" stroke-width="1.3" stroke-linecap="round"/>`,

  faction_human: `<polygon points="8,1 14.5,4 13.5,10.5 8,14.5 2.5,10.5 1.5,4" fill="#0f172a" stroke="#38bdf8" stroke-width="1" stroke-linejoin="round"/>
    <polygon points="8,3.2 12.2,5.2 11.5,9.6 8,12.5 4.5,9.6 3.8,5.2" fill="#0369a1" stroke="#facc15" stroke-width="0.8" stroke-linejoin="round"/>
    <polygon points="8,4.5 10.2,8 8,9.5 5.8,8" fill="#fef08a"/>`,

  faction_sul: `<polygon points="8,1.2 14.8,13 1.2,13" fill="#3b0764" stroke="#a855f7" stroke-width="1" stroke-linejoin="round"/>
    <polygon points="8,4 12.5,12 3.5,12" fill="#064e3b" stroke="#10b981" stroke-width="0.8" stroke-linejoin="round"/>
    <ellipse cx="8" cy="9.5" rx="2.2" ry="1.4" fill="#a7f3d0" stroke="#34d399" stroke-width="0.6"/>
    <circle cx="8" cy="9.5" r="0.8" fill="#064e3b"/>`,

  spell: `<circle cx="8" cy="8" r="6" stroke="#c084fc" stroke-width="0.9" opacity="0.6"/>
    <path d="M8 1.5C8 1.5 10 5.5 14.5 8C10 10.5 8 14.5 8 14.5C8 14.5 6 10.5 1.5 8C6 5.5 8 1.5 8 1.5Z" fill="#9333ea" stroke="#e9d5ff" stroke-width="0.9" stroke-linejoin="round"/>
    <circle cx="8" cy="8" r="1.8" fill="#fef08a"/>`,

  structure: `<polygon points="2,14 14,14 12.5,7 8,4.5 3.5,7" fill="#1e293b" stroke="#38bdf8" stroke-width="1" stroke-linejoin="round"/>
    <rect x="5.5" y="8.5" width="5" height="5.5" fill="#0f172a" stroke="#7dd3fc" stroke-width="0.75"/>
    <polygon points="8,1.5 10.5,4.5 5.5,4.5" fill="#38bdf8" stroke="#e0f2fe" stroke-width="0.6"/>
    <line x1="8" y1="8.5" x2="8" y2="14" stroke="#38bdf8" stroke-width="0.75"/>`,

  search: `<circle cx="6.5" cy="6.5" r="4.5" stroke="currentColor" stroke-width="1.4"/>
    <line x1="10" y1="10" x2="14.5" y2="14.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
    <circle cx="5.2" cy="5.2" r="1" fill="currentColor" opacity="0.6"/>`,

  energy: `<polygon points="9,1 2.5,9 8,9 7,15 13.5,7 8,7" fill="#fde047" stroke="#f59e0b" stroke-width="0.8" stroke-linejoin="round"/>
    <polygon points="8.5,3.2 4.5,8.5 8,8.5 7.4,12.5 11.5,7.5 8,7.5" fill="#ffffff" opacity="0.9"/>`,

  hourglass: `<path d="M3 1.5H13M3 14.5H13" stroke="#94a3b8" stroke-width="1.5" stroke-linecap="round"/>
    <path d="M4 2L8 7.5L12 2H4Z" fill="#1e293b" stroke="#64748b" stroke-width="0.9" stroke-linejoin="round"/>
    <path d="M4 14L8 8.5L12 14H4Z" fill="#1e293b" stroke="#64748b" stroke-width="0.9" stroke-linejoin="round"/>
    <polygon points="5.5,13.5 10.5,13.5 8,10" fill="#f59e0b"/>
    <circle cx="8" cy="8" r="0.75" fill="#fef08a"/>`,

  lock: `<rect x="3" y="6.5" width="10" height="8" rx="1.5" fill="#1e293b" stroke="#f59e0b" stroke-width="1"/>
    <path d="M5.5 6.5V4.5C5.5 3.12 6.62 2 8 2C9.38 2 10.5 3.12 10.5 4.5V6.5" stroke="#fbbf24" stroke-width="1.3" stroke-linecap="round"/>
    <circle cx="8" cy="10" r="1.2" fill="#f59e0b"/>
    <line x1="8" y1="10.5" x2="8" y2="12.5" stroke="#f59e0b" stroke-width="1.2" stroke-linecap="round"/>`,

  // =========================================================================
  // ONLINE LOBBY, MATCHMAKING & LEADERBOARDS
  // =========================================================================

  swords: `<path d="M2 14L5.5 10.5M3 10.5L6.5 14" stroke="#f87171" stroke-width="1" stroke-linecap="round"/>
    <path d="M14 2L6 10L4.5 8.5L12.5 0.5L14 2Z" fill="#ef4444" stroke="#fca5a5" stroke-width="0.75" stroke-linejoin="round"/>
    <path d="M14 14L10.5 10.5M13 10.5L9.5 14" stroke="#f87171" stroke-width="1" stroke-linecap="round"/>
    <path d="M2 2L10 10L11.5 8.5L3.5 0.5L2 2Z" fill="#ef4444" stroke="#fca5a5" stroke-width="0.75" stroke-linejoin="round"/>`,

  dice: `<polygon points="8,1.5 14,5 14,11 8,14.5 2,11 2,5" fill="#0f172a" stroke="#38bdf8" stroke-width="1" stroke-linejoin="round"/>
    <line x1="8" y1="1.5" x2="8" y2="8" stroke="#0284c7" stroke-width="0.8"/>
    <line x1="8" y1="8" x2="14" y2="5" stroke="#0284c7" stroke-width="0.8"/>
    <line x1="8" y1="8" x2="2" y2="5" stroke="#0284c7" stroke-width="0.8"/>
    <line x1="8" y1="8" x2="8" y2="14.5" stroke="#0284c7" stroke-width="0.8"/>
    <circle cx="5" cy="4" r="0.75" fill="#38bdf8"/>
    <circle cx="11" cy="4" r="0.75" fill="#38bdf8"/>
    <circle cx="5" cy="8" r="0.75" fill="#7dd3fc"/>
    <circle cx="5" cy="10.5" r="0.75" fill="#7dd3fc"/>
    <circle cx="11" cy="8" r="0.75" fill="#7dd3fc"/>
    <circle cx="11" cy="10.5" r="0.75" fill="#7dd3fc"/>
    <circle cx="8" cy="9.25" r="0.75" fill="#ffffff"/>`,

  link: `<path d="M6.5 9.5L9.5 6.5M7 5L8.5 3.5C9.88 2.12 12.12 2.12 13.5 3.5C14.88 4.88 14.88 7.12 13.5 8.5L12 10M9 11L7.5 12.5C6.12 13.88 3.88 13.88 2.5 12.5C1.12 11.12 1.12 8.88 2.5 7.5L4 6" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>`,

  plug: `<rect x="4" y="5" width="8" height="6" rx="1.5" fill="#1e293b" stroke="#38bdf8" stroke-width="1"/>
    <line x1="6" y1="2" x2="6" y2="5" stroke="#7dd3fc" stroke-width="1.4" stroke-linecap="round"/>
    <line x1="10" y1="2" x2="10" y2="5" stroke="#7dd3fc" stroke-width="1.4" stroke-linecap="round"/>
    <path d="M8 11V14.5" stroke="#38bdf8" stroke-width="1.4" stroke-linecap="round"/>
    <circle cx="8" cy="8" r="1" fill="#00e5ff"/>`,

  skull: `<path d="M3 7C3 4.24 5.24 2 8 2C10.76 2 13 4.24 13 7C13 8.8 12.1 10.3 10.7 11.2L10.5 13.5H5.5L5.3 11.2C3.9 10.3 3 8.8 3 7Z" fill="#1e293b" stroke="#ef4444" stroke-width="1" stroke-linejoin="round"/>
    <circle cx="6" cy="7" r="1.3" fill="#ef4444"/>
    <circle cx="10" cy="7" r="1.3" fill="#ef4444"/>
    <polygon points="8,8.5 7.2,10 8.8,10" fill="#fca5a5"/>
    <line x1="6.5" y1="12" x2="6.5" y2="13.5" stroke="#ef4444" stroke-width="0.8"/>
    <line x1="8" y1="12" x2="8" y2="13.5" stroke="#ef4444" stroke-width="0.8"/>
    <line x1="9.5" y1="12" x2="9.5" y2="13.5" stroke="#ef4444" stroke-width="0.8"/>`,

  trophy: `<path d="M3.5 2H12.5V7C12.5 9.5 10.5 11.5 8 11.5C5.5 11.5 3.5 9.5 3.5 7V2Z" fill="#ca8a04" stroke="#facc15" stroke-width="1" stroke-linejoin="round"/>
    <path d="M3.5 3.5H1.5C1.5 5.5 2.5 7 3.5 7M12.5 3.5H14.5C14.5 5.5 13.5 7 12.5 7" stroke="#fde047" stroke-width="0.9" stroke-linecap="round"/>
    <path d="M6.5 11.5V13.5H9.5V11.5M5 14.5H11" stroke="#eab308" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
    <polygon points="8,4 8.8,5.8 10.8,5.8 9.2,7 9.8,8.8 8,7.6 6.2,8.8 6.8,7 5.2,5.8 7.2,5.8" fill="#fef08a"/>`,

  warning: `<path d="M8 1.5L15 13.5H1L8 1.5Z" fill="#451a03" stroke="#f59e0b" stroke-width="1" stroke-linejoin="round"/>
    <line x1="8" y1="5.5" x2="8" y2="9.5" stroke="#fef08a" stroke-width="1.5" stroke-linecap="round"/>
    <circle cx="8" cy="11.5" r="0.9" fill="#fef08a"/>`,

  edit_pen: `<path d="M11.5 1.5L14.5 4.5L5 14H2V11L11.5 1.5Z" fill="#1e293b" stroke="currentColor" stroke-width="1" stroke-linejoin="round"/>
    <line x1="9.5" y1="3.5" x2="12.5" y2="6.5" stroke="currentColor" stroke-width="0.9"/>`,

  // Rank Badges
  rank_cadet: `<path d="M2.5 10.5L8 5L13.5 10.5L12 12L8 8L4 12L2.5 10.5Z" fill="#94a3b8" stroke="#cbd5e1" stroke-width="0.8" stroke-linejoin="round"/>`,

  rank_gold: `<path d="M2.5 8L8 2.5L13.5 8L12 9.5L8 5.5L4 9.5L2.5 8Z" fill="#eab308" stroke="#fde047" stroke-width="0.8" stroke-linejoin="round"/>
    <path d="M2.5 12L8 6.5L13.5 12L12 13.5L8 9.5L4 13.5L2.5 12Z" fill="#ca8a04" stroke="#fef08a" stroke-width="0.8" stroke-linejoin="round"/>`,

  rank_platinum: `<polygon points="8,1 15,6 13,11 8,14.5 3,11 1,6" fill="#0f172a" stroke="#38bdf8" stroke-width="1" stroke-linejoin="round"/>
    <polygon points="8,3.5 12.5,7 8,11.5 3.5,7" fill="#0284c7" stroke="#e0f2fe" stroke-width="0.8" stroke-linejoin="round"/>
    <circle cx="8" cy="7.5" r="1.5" fill="#ffffff"/>`,

  rank_diamond: `<polygon points="8,1 14.5,5.5 8,15 1.5,5.5" fill="#082f49" stroke="#00f0ff" stroke-width="1" stroke-linejoin="round"/>
    <polygon points="8,1 11.5,5.5 8,15 4.5,5.5" fill="#0284c7" stroke="#7dd3fc" stroke-width="0.75"/>
    <line x1="1.5" y1="5.5" x2="14.5" y2="5.5" stroke="#e0f2fe" stroke-width="0.8"/>
    <circle cx="8" cy="5.5" r="1" fill="#ffffff"/>`,

  rank_master: `<polygon points="8,0.5 13.5,3.5 15.5,9.5 11.5,14.5 4.5,14.5 0.5,9.5 2.5,3.5" fill="#3b0764" stroke="#c084fc" stroke-width="1" stroke-linejoin="round"/>
    <polygon points="8,3.5 11.5,8 8,12.5 4.5,8" fill="#9333ea" stroke="#f3e8ff" stroke-width="0.8" stroke-linejoin="round"/>
    <circle cx="8" cy="8" r="1.2" fill="#fef08a"/>`,

  rank_grandmaster: `<polygon points="1,14 15,14 14.5,5 11,9 8,2 5,9 1.5,5" fill="#991b1b" stroke="#f59e0b" stroke-width="1" stroke-linejoin="round"/>
    <polygon points="3.5,13 12.5,13 12,6.5 9.8,9 8,4.5 6.2,9 4,6.5" fill="#f59e0b"/>
    <circle cx="8" cy="2" r="1" fill="#ffffff"/>
    <circle cx="1.5" cy="5" r="0.8" fill="#fef08a"/>
    <circle cx="14.5" cy="5" r="0.8" fill="#fef08a"/>`,

  // =========================================================================
  // DIRECTIVES, PACKS, CARGO BAY & THEMES
  // =========================================================================

  flame: `<path d="M8 1C8 1 10.5 4 10.5 6.5C10.5 7.2 10.2 7.8 9.8 8.3C10.8 8.6 13 9.8 13 12C13 13.8 11.2 15 8 15C4.8 15 3 13.8 3 12C3 9.5 5.5 8 5.5 6C5.5 4.5 4.5 3.5 4.5 3.5C4.5 3.5 7 3.5 8 1Z" fill="#ea580c" stroke="#f97316" stroke-width="0.8" stroke-linejoin="round"/>
    <path d="M8 8C8 8 9.5 9.5 9.5 11C9.5 12.2 8.8 13.5 8 13.5C7.2 13.5 6.5 12.2 6.5 11C6.5 9.8 8 8 8 8Z" fill="#fde047"/>`,

  freeze: `<line x1="8" y1="1" x2="8" y2="15" stroke="#38bdf8" stroke-width="1.2" stroke-linecap="round"/>
    <line x1="1" y1="8" x2="15" y2="8" stroke="#38bdf8" stroke-width="1.2" stroke-linecap="round"/>
    <line x1="3" y1="3" x2="13" y2="13" stroke="#38bdf8" stroke-width="1.2" stroke-linecap="round"/>
    <line x1="3" y1="13" x2="13" y2="3" stroke="#38bdf8" stroke-width="1.2" stroke-linecap="round"/>
    <circle cx="8" cy="8" r="1.8" fill="#e0f2fe" stroke="#0284c7" stroke-width="0.6"/>`,

  crate: `<polygon points="8,1.5 14.5,5 14.5,11.5 8,15 1.5,11.5 1.5,5" fill="#0f172a" stroke="#38bdf8" stroke-width="1" stroke-linejoin="round"/>
    <line x1="8" y1="1.5" x2="8" y2="15" stroke="#0284c7" stroke-width="0.8"/>
    <line x1="1.5" y1="5" x2="8" y2="8.25" stroke="#0284c7" stroke-width="0.8"/>
    <line x1="14.5" y1="5" x2="8" y2="8.25" stroke="#0284c7" stroke-width="0.8"/>
    <polygon points="8,3.2 12,5.2 8,7.2 4,5.2" fill="#0284c7" opacity="0.6"/>
    <circle cx="8" cy="8.25" r="1" fill="#38bdf8"/>`,

  crown: `<polygon points="2,13 14,13 13.5,5 10,8.5 8,3 6,8.5 2.5,5" fill="#eab308" stroke="#fef08a" stroke-width="0.9" stroke-linejoin="round"/>
    <rect x="2" y="12" width="12" height="2" rx="0.5" fill="#ca8a04" stroke="#fef08a" stroke-width="0.6"/>
    <circle cx="8" cy="3" r="0.9" fill="#ffffff"/>
    <circle cx="2.5" cy="5" r="0.75" fill="#ef4444"/>
    <circle cx="13.5" cy="5" r="0.75" fill="#ef4444"/>`,

  // Theme Icons for Arsenal Progress Tabs
  theme_fauna: `<path d="M8 1.5C8 1.5 13 4 13 9.5C13 12.5 10.5 14.5 8 14.5C5.5 14.5 3 12.5 3 9.5C3 4 8 1.5 8 1.5Z" fill="#064e3b" stroke="#10b981" stroke-width="1"/>
    <line x1="8" y1="4" x2="8" y2="13.5" stroke="#34d399" stroke-width="0.9"/>
    <path d="M8 7L11 5.5M8 9.5L11.5 8M8 11.5L10.5 10.5M8 7L5 5.5M8 9.5L4.5 8M8 11.5L5.5 10.5" stroke="#34d399" stroke-width="0.8"/>`,

  theme_sul: `<polygon points="8,1.2 14.8,13 1.2,13" fill="#3b0764" stroke="#a855f7" stroke-width="1" stroke-linejoin="round"/>
    <polygon points="8,4 12.5,12 3.5,12" fill="#064e3b" stroke="#10b981" stroke-width="0.8" stroke-linejoin="round"/>
    <ellipse cx="8" cy="9.5" rx="2.2" ry="1.4" fill="#a7f3d0" stroke="#34d399" stroke-width="0.6"/>
    <circle cx="8" cy="9.5" r="0.8" fill="#064e3b"/>`,

  theme_ballistics: `<path d="M2 14L5.5 10.5M3 10.5L6.5 14" stroke="#f87171" stroke-width="1" stroke-linecap="round"/>
    <path d="M14 2L6 10L4.5 8.5L12.5 0.5L14 2Z" fill="#ef4444" stroke="#fca5a5" stroke-width="0.75" stroke-linejoin="round"/>
    <path d="M14 14L10.5 10.5M13 10.5L9.5 14" stroke="#f87171" stroke-width="1" stroke-linecap="round"/>
    <path d="M2 2L10 10L11.5 8.5L3.5 0.5L2 2Z" fill="#ef4444" stroke="#fca5a5" stroke-width="0.75" stroke-linejoin="round"/>`,

  theme_plasma: `<polygon points="9,1 2.5,9 8,9 7,15 13.5,7 8,7" fill="#fde047" stroke="#f59e0b" stroke-width="0.8" stroke-linejoin="round"/>
    <polygon points="8.5,3.2 4.5,8.5 8,8.5 7.4,12.5 11.5,7.5 8,7.5" fill="#ffffff" opacity="0.9"/>`,

  theme_defense: `<path d="M8 1.5L14 3.8V8.2C14 11.5 11.2 13.9 8 15C4.8 13.9 2 11.5 2 8.2V3.8L8 1.5Z" fill="#082f49" stroke="#38bdf8" stroke-width="1" stroke-linejoin="round"/>
    <polygon points="8,4.5 11.5,6.5 11 9.5 8,12 5,9.5 4.5,6.5" fill="#0284c7" stroke="#e0f2fe" stroke-width="0.8"/>`,

  theme_fortress: `<path d="M8 1.5L14 3.8V8.2C14 11.5 11.2 13.9 8 15C4.8 13.9 2 11.5 2 8.2V3.8L8 1.5Z" fill="#082f49" stroke="#38bdf8" stroke-width="1" stroke-linejoin="round"/>
    <polygon points="8,4.5 11.5,6.5 11 9.5 8,12 5,9.5 4.5,6.5" fill="#0284c7" stroke="#e0f2fe" stroke-width="0.8"/>`,

  theme_stealth: `<polygon points="8,1.5 14,4.5 11.5,14 4.5,14 2,4.5" fill="#090d16" stroke="#475569" stroke-width="1" stroke-linejoin="round"/>
    <path d="M3.5 6.5C5.5 8.5 10.5 8.5 12.5 6.5" stroke="#94a3b8" stroke-width="1.2" stroke-linecap="round"/>
    <circle cx="5.5" cy="5.5" r="0.8" fill="#38bdf8"/>
    <circle cx="10.5" cy="5.5" r="0.8" fill="#38bdf8"/>`,

  theme_cosmic: `<circle cx="8" cy="8" r="6" stroke="#6366f1" stroke-width="0.8" opacity="0.6"/>
    <polygon points="8,1 9.5,6.5 15,8 9.5,9.5 8,15 6.5,9.5 1,8 6.5,6.5" fill="#818cf8" stroke="#e0e7ff" stroke-width="0.8" stroke-linejoin="round"/>
    <circle cx="8" cy="8" r="1.8" fill="#ffffff"/>`,

  theme_loot: `<polygon points="8,1 14.5,5.5 8,15 1.5,5.5" fill="#082f49" stroke="#00f0ff" stroke-width="1" stroke-linejoin="round"/>
    <polygon points="8,1 11.5,5.5 8,15 4.5,5.5" fill="#0284c7" stroke="#7dd3fc" stroke-width="0.75"/>
    <line x1="1.5" y1="5.5" x2="14.5" y2="5.5" stroke="#e0f2fe" stroke-width="0.8"/>
    <circle cx="8" cy="5.5" r="1" fill="#ffffff"/>`,

  theme_fleet: `<path d="M3.5 2H12.5V7C12.5 9.5 10.5 11.5 8 11.5C5.5 11.5 3.5 9.5 3.5 7V2Z" fill="#ca8a04" stroke="#facc15" stroke-width="1" stroke-linejoin="round"/>
    <path d="M6.5 11.5V13.5H9.5V11.5M5 14.5H11" stroke="#eab308" stroke-width="1.2" stroke-linecap="round"/>`,

  // =========================================================================
  // SYSTEM, AUDIO & TAUNTS
  // =========================================================================

  sound_sfx: `<polygon points="2,5 5,5 9,2 9,14 5,11 2,11" fill="#1e293b" stroke="currentColor" stroke-width="1" stroke-linejoin="round"/>
    <path d="M11.5 6C12.5 6.8 13 7.8 13 8C13 8.2 12.5 9.2 11.5 10" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>`,

  sound_all: `<polygon points="2,5 5,5 9,2 9,14 5,11 2,11" fill="#1e293b" stroke="currentColor" stroke-width="1" stroke-linejoin="round"/>
    <path d="M11.5 5.5C12.8 6.5 13.5 7.5 13.5 8C13.5 8.5 12.8 9.5 11.5 10.5M13.5 3.5C15.2 5 16 6.8 16 8C16 9.2 15.2 11 13.5 12.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>`,

  sound_mute: `<polygon points="2,5 5,5 9,2 9,14 5,11 2,11" fill="#1e293b" stroke="currentColor" stroke-width="1" stroke-linejoin="round"/>
    <line x1="2" y1="2" x2="14" y2="14" stroke="#ef4444" stroke-width="1.6" stroke-linecap="round"/>`,

  robot: `<rect x="3" y="4" width="10" height="9" rx="2" fill="#1e293b" stroke="#38bdf8" stroke-width="1"/>
    <line x1="8" y1="1" x2="8" y2="4" stroke="#38bdf8" stroke-width="1.2" stroke-linecap="round"/>
    <circle cx="8" cy="1" r="0.8" fill="#fde047"/>
    <circle cx="5.5" cy="7.5" r="1.2" fill="#00f0ff"/>
    <circle cx="10.5" cy="7.5" r="1.2" fill="#00f0ff"/>
    <line x1="5.5" y1="10.5" x2="10.5" y2="10.5" stroke="#94a3b8" stroke-width="0.8" stroke-linecap="round"/>`,

  stopwatch: `<circle cx="8" cy="9" r="6" fill="#1e293b" stroke="#38bdf8" stroke-width="1"/>
    <line x1="8" y1="1" x2="8" y2="3" stroke="#94a3b8" stroke-width="1.5" stroke-linecap="round"/>
    <line x1="6" y1="1" x2="10" y2="1" stroke="#94a3b8" stroke-width="1.2" stroke-linecap="round"/>
    <line x1="8" y1="9" x2="8" y2="5.5" stroke="#fde047" stroke-width="1.2" stroke-linecap="round"/>
    <line x1="8" y1="9" x2="11" y2="9" stroke="#38bdf8" stroke-width="1.2" stroke-linecap="round"/>
    <circle cx="8" cy="9" r="0.9" fill="#ffffff"/>`,

  rocket: `<path d="M8 1C8 1 12 3.5 12 8.5L9.5 11L8 9L6.5 11L4 8.5C4 3.5 8 1 8 1Z" fill="#1e293b" stroke="#38bdf8" stroke-width="0.9" stroke-linejoin="round"/>
    <circle cx="8" cy="5.5" r="1.2" fill="#fde047"/>
    <polygon points="4,9 2,12 5,11.5" fill="#0284c7" stroke="#38bdf8" stroke-width="0.6"/>
    <polygon points="12,9 14,12 11,11.5" fill="#0284c7" stroke="#38bdf8" stroke-width="0.6"/>
    <polygon points="8,11 6.5,15 8,13.5 9.5,15" fill="#f97316"/>`,

  avatar_placeholder: `<circle cx="8" cy="5" r="3" fill="#1e293b" stroke="#94a3b8" stroke-width="1"/>
    <path d="M2.5 14.5C2.5 11.5 5 9.5 8 9.5C11 9.5 13.5 11.5 13.5 14.5" fill="#1e293b" stroke="#94a3b8" stroke-width="1"/>`,

  taunt_wave: `<path d="M5 7V3C5 2.45 5.45 2 6 2C6.55 2 7 2.45 7 3V6.5M7 3C7 2.45 7.45 2 8 2C8.55 2 9 2.45 9 3V6.5M9 3.5C9 2.95 9.45 2.5 10 2.5C10.55 2.5 11 2.95 11 3.5V7M11 5C11 4.45 11.45 4 12 4C12.55 4 13 4.45 13 5V9.5C13 12.5 10.5 14.5 8 14.5C5.5 14.5 3.5 13 3 10L2 7.5C1.8 7 2.2 6.5 2.8 6.7L5 8" stroke="#facc15" stroke-width="0.9" stroke-linejoin="round"/>`,

  taunt_comms: `<path d="M2 14L8 8M8 8L14 2M8 8C5.5 5.5 5 2 5 2C5 2 8.5 2.5 11 5" stroke="#38bdf8" stroke-width="1.1" stroke-linecap="round" stroke-linejoin="round"/>
    <circle cx="11" cy="5" r="1.5" fill="#fde047"/>
    <line x1="1" y1="15" x2="5" y2="15" stroke="#94a3b8" stroke-width="1.2" stroke-linecap="round"/>`,

  taunt_handshake: `<path d="M1.5 6.5L4.5 4L8 6.5L6.5 8M14.5 6.5L11.5 4L8 6.5L9.5 8" stroke="#facc15" stroke-width="1" stroke-linejoin="round"/>
    <path d="M5.5 8L8 10L10.5 8M7 9.5L8.5 11M9 9.5L7.5 11" stroke="#38bdf8" stroke-width="0.9" stroke-linecap="round"/>
    <rect x="1" y="6" width="3" height="3" fill="#1e293b" stroke="#64748b" stroke-width="0.8"/>
    <rect x="12" y="6" width="3" height="3" fill="#1e293b" stroke="#64748b" stroke-width="0.8"/>`,

  taunt_wrench: `<path d="M13.5 5.5C12.5 5.5 11.8 4.8 11.8 3.8C11.8 3 12.2 2.4 12.8 2C11.8 1.4 10.5 1.5 9.5 2.2C8.2 3.2 8 5 8.8 6.2L2.5 12.5C2 13 2 13.8 2.5 14.3C3 14.8 3.8 14.8 4.3 14.3L10.6 8C11.8 8.8 13.6 8.6 14.6 7.3C15.3 6.3 15.4 5 14.8 4C14.4 4.6 13.8 5 13 5" stroke="#94a3b8" stroke-width="1.1" stroke-linejoin="round" fill="#1e293b"/>`,

  taunt_satellite: `<rect x="6" y="6" width="4" height="4" rx="0.8" fill="#0284c7" stroke="#38bdf8" stroke-width="0.8"/>
    <rect x="1" y="6.5" width="4" height="3" fill="#0f172a" stroke="#7dd3fc" stroke-width="0.75"/>
    <rect x="11" y="6.5" width="4" height="3" fill="#0f172a" stroke="#7dd3fc" stroke-width="0.75"/>
    <line x1="8" y1="2" x2="8" y2="6" stroke="#fde047" stroke-width="0.8"/>
    <circle cx="8" cy="2" r="0.8" fill="#fde047"/>
    <path d="M8 10L9.5 14M8 10L6.5 14" stroke="#38bdf8" stroke-width="0.8"/>`,

  taunt_radiation: `<circle cx="8" cy="8" r="1.8" fill="#fde047" stroke="#f59e0b" stroke-width="0.6"/>
    <path d="M8 5.5V1.5C6.2 1.5 4.5 2.2 3.3 3.3L6.1 6.5C6.5 6 7.2 5.6 8 5.5Z" fill="#f59e0b"/>
    <path d="M10.2 9.2L13.7 11.2C14.6 9.6 14.8 7.7 14.3 5.9L10.3 7.4C10.5 8 10.5 8.7 10.2 9.2Z" fill="#f59e0b"/>
    <path d="M5.8 9.2L2.3 11.2C3.2 12.8 4.7 13.9 6.5 14.4L7.5 10.3C6.8 10.1 6.2 9.7 5.8 9.2Z" fill="#f59e0b"/>`,

  bubble: `<path d="M2.5 3C2.5 2.17 3.17 1.5 4 1.5H12C12.83 1.5 13.5 2.17 13.5 3V9C13.5 9.83 12.83 10.5 12 10.5H6L3 13.5V10.5H4C3.17 10.5 2.5 9.83 2.5 9V3Z" fill="#0f172a" stroke="#38bdf8" stroke-width="1" stroke-linejoin="round"/>
    <circle cx="5.5" cy="6" r="0.8" fill="#e0f2fe"/>
    <circle cx="8" cy="6" r="0.8" fill="#e0f2fe"/>
    <circle cx="10.5" cy="6" r="0.8" fill="#e0f2fe"/>`
};

/**
 * Returns a standardized, self-contained SVG icon markup string.
 *
 * @param {string} name - Name of the registered icon
 * @param {number|string} [size=16] - Size in px (number) or preset string ('sm', 'md', etc.)
 * @param {string} [extraClass=''] - Additional CSS classes
 * @param {string} [extraStyle=''] - Inline CSS style string
 * @returns {string} The SVG element HTML string
 */
export function getIconSvg(name, size = 16, extraClass = '', extraStyle = '') {
  const content = ICON_REGISTRY[name];
  if (!content) {
    return '';
  }

  const px = typeof size === 'number'
    ? size
    : (SIZE_PRESETS[size] || parseInt(size, 10) || 16);

  const classes = extraClass
    ? `ucg-icon ucg-icon-${name} ${extraClass}`
    : `ucg-icon ucg-icon-${name}`;

  const styleAttr = extraStyle ? ` style="${extraStyle}"` : '';

  return `<svg class="${classes}" width="${px}" height="${px}" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"${styleAttr}>${content}</svg>`;
}

/**
 * Check if an icon is registered.
 * @param {string} name
 * @returns {boolean}
 */
export function hasIcon(name) {
  return Object.prototype.hasOwnProperty.call(ICON_REGISTRY, name);
}

/**
 * Get an array of all registered icon names.
 * @returns {string[]}
 */
export function getRegisteredIconNames() {
  return Object.keys(ICON_REGISTRY);
}
